import {colors} from '@commercial-helios/ui/theme'
import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import Footer from '@/components/footer'
import Header from '@/components/header'
import {PodcastForm} from '@/components/lead-form/podcast-form'
import SEO from '@/components/seo'
import type {PodcastFormPageQuery} from '@/graphql-types'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import Image from '@helios/shared/components/image'
import {Main} from '@helios/shared/components/skip-to-content'
import {Layout, container, Section} from '@helios/shared/styles/grid'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'
import mq from '@helios/shared/utils/media-query'

type Props = PageProps<PodcastFormPageQuery>

const PodcastFormPage = (props: Props) => {
    const content = props.data.prismicPodcastFormPage.data

    return (
        <BackgroundWrapper>
            <Header />

            <Main>
                <StyledSection>
                    <StyledLayout variant="two-asymmetrical" mirrored alignItems="center">
                        <Content>
                            <StyledImage image={content.image} width={540} />
                        </Content>

                        <PodcastForm
                            title={content.form_title}
                            confirmationPage={content.confirmation_page}
                            hubspotProperties={{
                                campaign__updating_: content.hubspot_properties[0].campaign,
                            }}
                        />
                    </StyledLayout>
                </StyledSection>
            </Main>

            <Footer />
        </BackgroundWrapper>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<PodcastFormPageQuery>) => {
    const pageData = data.prismicPodcastFormPage.data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
            preventIndexing={pageData.exclude_robots}
        />
    )
}

const MOBILE_BREAKPOINT = 'xs'

const BackgroundWrapper = styled.div`
    background: ${colors.background.default};

    ${mq.min(MOBILE_BREAKPOINT)} {
        background: ${colors.background.tertiary};
    }
`

const StyledSection = styled(Section)`
    ${mq.max(MOBILE_BREAKPOINT)} {
        padding-right: 0;
        padding-left: 0;
    }
`

const StyledLayout = styled(Layout)`
    ${mq.max(MOBILE_BREAKPOINT)} {
        row-gap: 0;
    }

    ${mq.max('md')} {
        grid-template-columns: 1fr;
    }
`

const Content = styled.div`
    display: grid;
    justify-items: center;

    ${mq.max(MOBILE_BREAKPOINT)} {
        ${container}
    }
`

const StyledImage = styled(Image)`
    width: 100%;
    max-width: 540px;

    ${mq.max(MOBILE_BREAKPOINT)} {
        background: ${colors.background.tertiary};
    }
`

export const query = graphql`
    query PodcastFormPage($uid: String!, $locale: String!) {
        prismicPodcastFormPage(uid: {eq: $uid}, lang: {eq: $locale}) {
            _previewable
            data {
                meta_title
                meta_description
                exclude_robots
                open_graph_image {
                    url
                }
                form_title
                image {
                    dimensions {
                        height
                        width
                    }
                    alt
                    copyright
                    url
                }
                hubspot_properties {
                    campaign
                }
                confirmation_page {
                    ...PrismicLink
                }
            }
        }
    }
`

export default withPrismicPreview(PodcastFormPage)
